import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React from 'react';
import { RHFInputText, RHFInputTextarea } from '../../../components/rhf';
import type { FieldArrayWithId, Control, FieldValues } from 'react-hook-form';
import './TranslationTable.css';
   
type TranslationColumn = {
    field: string;
    header: string;
    required: boolean;
    inputType?: 'text' | 'textarea';
    rows?: number;
};

type IdentityColumn = {
    field: string;
    header: string;
};

type TranslationTableVariant = 'compact' | 'entity';

export interface TranslationTableProps<TFormValues extends FieldValues> {
    fields: FieldArrayWithId<TFormValues, any>[];
    control: Control<TFormValues>; 
    arrayName: string;
    translationColumns: TranslationColumn[];
    identityColumns?: IdentityColumn[];
    variant?: TranslationTableVariant;
}

export const TranslationTable = <TFormValues extends FieldValues>({
    fields,
    control,
    arrayName,
    translationColumns = [],
    identityColumns = [],
    variant = 'compact'
}: TranslationTableProps<TFormValues>) => {
    const isEntity = variant === 'entity';
    const identityTemplate = (rowData: Record<string, unknown>, col: IdentityColumn) => {
        const value = rowData[col.field];
        return <span>{value == null || value === '' ? '—' : String(value)}</span>;
    };

    const inputTemplate = (rowIndex: number, col: TranslationColumn) => {
        const fieldName = `${arrayName}.${rowIndex}.${String(col.field)}`
        const commonProps = {
            control,
            name: fieldName,
            rules: { required: col.required ? 'Vyplňte překlad' : false }
        };

        if (col.inputType === 'textarea') {
            return (
                <div className='translation-table__input-wrap'>
                    <RHFInputTextarea
                        {...commonProps}
                        inputProps={{
                            rows: col.rows ?? 2,
                            style: { width: '100%', resize: 'vertical' }
                        }}
                    />
                </div>
            );
        }

        return (
            <div className='translation-table__input-wrap'>
                <RHFInputText
                    {...commonProps}
                    inputProps={{ style: { width: '100%' } }}
                />
            </div>
        );
    };

    return (
        <div className={`translation-table translation-table--${variant}`}>
            <DataTable
                value={fields}
                dataKey="id"
                size="small"
                stripedRows
                emptyMessage="Žádná data nebyla nalezena"
                className='p-datatable-sm translation-table__datatable'
            >
                {identityColumns.map((col) => (
                    <Column
                        key={col.field}
                        header={col.header}
                        body={(rowData) => identityTemplate(rowData, col)}
                    />
                ))}
                {translationColumns.map((col) => (
                    <Column
                        key={col.field}
                        header={col.required ? `${col.header} *` : col.header}
                        body={(_, options) => inputTemplate(options.rowIndex, col)}
                        style={{
                            minWidth: isEntity ? '260px' : '180px'
                        }}
                    />
                ))}
            </DataTable>
        </div>
    );
};
